import * as React from "react";
import { createPortal } from "react-dom";
import { useRef, useState, useEffect, useLayoutEffect } from "react";
import { useRedraw } from "../models/useRerender";
import WatchableValue from "../models/WatchableValue";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";

export type EditSelectOption = { label: string; value: string };

export type EditSelectProps = {
  id?: string;
  ariaLabel?: string;
  dropdownPlacement?:
    | "top" // no itx
    | "bottom";
  value?: string;
  watchableValue?: WatchableValue<string>;
  onChange?: (value: string) => void;
  options: WatchableValue<EditSelectOption[] | undefined>;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
};

const dropdownStyleBase: React.CSSProperties = {
  position: "fixed", // no itx
  margin: 0,
  padding: 0,
  listStyle: "none",
  maxHeight: "200px",
  overflowY: "auto",
  zIndex: 10000,
  border: "1px solid var(--border-color, #ccc)",
  borderRadius: "4px",
  backgroundColor: "var(--panel-background, #fff)",
  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
};

/**
 * Combobox: Eingabefeld mit optionaler Dropdown-Liste.
 * Erlaubt sowohl Auswahl aus options als auch freies Eintippen von Text.
 * Dropdown wird per Portal gerendert, damit er nicht von overflow abgeschnitten wird.
 */
export default function EditSelect(props: EditSelectProps) {
  const { id, ariaLabel, dropdownPlacement = "bottom", value, watchableValue, onChange, options, placeholder = "", className, style } = props;
  useOnChangeWatcher(options);

  const optionValues = options.value;
  const isOpen = useRef<boolean>(false);
  const [filterTermForDropdown, setFilterTermForDropdown] = useState("");
  const redraw = useRedraw();
  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);

  /** Anchors the menu to the current field position whenever it opens. */
  function setIsOpen(open: boolean) {
    isOpen.current = open;
    if (open && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownRect({
        top: dropdownPlacement === "top" ? rect.top : rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
    if (dropdownRef.current) {
      dropdownRef.current.style.display = open ? "block" : "none";
    }
  }

  function getIsOpen(): boolean {
    if (dropdownRef.current) {
      return dropdownRef.current.style.display !== "none";
    }
    return false;
  }

  let optionsAreMenuOnly = true;
  if (optionValues) {
    for (const o of optionValues) {
      if (!o.value.startsWith("__")) {
        optionsAreMenuOnly = false;
        break;
      }
    }
  }

  function getInputText(): string {
    let v = inputRef.current?.innerText ?? "";
    v = v.trim();
    return v;
  }

  const displayText = React.useMemo(() => {
    if (!optionValues) return value;
    const opt = optionValues.find((o) => o.value === value);
    if (opt?.value.startsWith("__")) return "";
    return opt ? opt.label : value;
  }, [value, optionValues]);

  function setValueInsideDom() {
    let v = value || watchableValue?.value || "";

    if (v.startsWith("__")) {
      v = "";
    }

    if (inputRef.current) {
      const changed = inputRef.current.innerText != v;
      // only touch the DOM when the text actually differs, otherwise the caret jumps to the start on every keystroke
      if (changed) {
        inputRef.current.innerText = v;
        redraw();
      }
    }
  }

  useEffect(() => {
    setValueInsideDom();
  }, [value]);

  useLayoutEffect(() => {
    if (!isOpen || !inputRef.current) return;
    const el = inputRef.current;
    const rect = el.getBoundingClientRect();
    setDropdownRect({
      top: dropdownPlacement === "top" ? rect.top : rect.bottom,
      left: rect.left,
      width: rect.width,
    });
  }, [isOpen, dropdownPlacement]);

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        const target = e.target as HTMLElement;
        if (target.closest("[data-editselect-dropdown]")) return;
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useOnChangeWatcher(watchableValue, () => {
    setValueInsideDom();
    return false;
  });

  const filteredOptions = React.useMemo(() => {
    if (!filterTermForDropdown) return optionValues;
    if (!optionValues) return [];
    return optionValues.filter(
      (o) => o.value.startsWith("__") || o.label.toLowerCase().includes(filterTermForDropdown),
    );
  }, [optionValues, filterTermForDropdown]);

  function handleInputChange(e: React.FormEvent<HTMLDivElement>) {
    const term = e.currentTarget.innerText.trim().toLowerCase();
    setFilterTermForDropdown(term);
  }

  function handleSelect(opt: EditSelectOption) {
    if (inputRef.current) {
      if (opt.value.startsWith("__")) {
        if (opt.value === "__-") {
          return;
        } else {
          inputRef.current.innerText = "";
        }
      } else {
        inputRef.current.innerText = opt.label;
      }
    }

    if (watchableValue) {
      watchableValue.value = getInputText();
    }

    if (onChange) {
      onChange(opt.value);
    }

    setIsOpen(false);
  }

  function handleFocus() {
    setFilterTermForDropdown("");
    setIsOpen(true);
  }

  function handleBlur() {
    // onChange(getInputText());
  }

  function handleMouseDown(ev: React.MouseEvent<HTMLDivElement>) {
    if (isOpen.current) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
      handleFocus();
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      setIsOpen(false);
    }

    setIsOpen(false);
  }

  function handleChange() {
    if (watchableValue) {
      watchableValue.value = getInputText();
    }

    if (onChange) {
      onChange(getInputText());
    }
  }

  const optionItems = filteredOptions?.map(opt => (
    <li className={opt.value.startsWith("__") ? "filterMenu" : "filterItem"}
      key={opt.value} role="option" aria-selected={opt.value === value}
      onClick={() => handleSelect(opt)} onMouseDown={event => event.preventDefault()}>
      {opt.label}
    </li>
  ));

  const dropdownContent = dropdownRect !== null && filteredOptions && optionValues && (
    <ul
      ref={dropdownRef}
      data-editselect-dropdown
      className="select editSelectDropdown"
      style={{
        display: isOpen.current ? "block" : "none",
        ...dropdownStyleBase,
        top: dropdownPlacement === "bottom" ? dropdownRect.top : undefined,
        bottom: dropdownPlacement === "top" ? window.innerHeight - dropdownRect.top : undefined,
        left: dropdownRect.left,
        // minWidth: dropdownRect.width,
        width: "max-content",
      }}
    >
      {optionItems}
    </ul>
  );

  return (
    <div ref={containerRef} className={"estadaRow editSelect " + className} style={{ ...style }}>
      {!value && !getInputText() && (
        <div
          className="estadaPlaceholder"
          style={{
            position: "absolute",
            top: "0px",
            left: "0px",
            padding: "5px",
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {placeholder}
        </div>
      )}

      <div
        ref={inputRef}
        contentEditable={true}
        suppressContentEditableWarning={true}
        id={id}
        aria-label={ariaLabel}
        className={className + " filterInput"}
        onInput={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onKeyUp={handleChange}
        onMouseDown={handleMouseDown}
        style={{
          minWidth: "50px",
          cursor: "text",
          userSelect: "text",
          padding: "5px",
          height: "100%",
          flexGrow: "1",
          justifyContent: "center",
        }}
      />
      {typeof document !== "undefined" && dropdownContent ? createPortal(dropdownContent, document.body) : null}
    </div>
  );
}
